import type { PackagePlace } from './api/packages.types';

export interface PackageSearchValues {
  origin: PackagePlace | null;
  destination: PackagePlace | null;
  month: number | null;
  year: number | null;
  adults: number;
}

export const EMPTY_PACKAGE_SEARCH: PackageSearchValues = {
  origin: null,
  destination: null,
  month: null,
  year: null,
  adults: 1,
};

/** Serializes a place's ids the way the results route expects them: `12,34`. */
export function placeIdsParam(place: PackagePlace | null): string {
  if (!place) return '';
  return place.ids
    .map((id) => String(id).trim())
    .filter(Boolean)
    .join(',');
}

function parseIds(raw: string | null): number[] {
  if (!raw) return [];

  return raw
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isFinite(id));
}

function parsePlace(idsParam: string | null, nameParam: string | null): PackagePlace | null {
  const ids = parseIds(idsParam);
  const name = (nameParam || '').trim();

  if (ids.length === 0 || !name) return null;

  return { ids, name };
}

/**
 * Reads the values a `/buscarxpaquetes` URL carries, so the hero form stays in
 * sync when a user lands on (or goes back to) a results page.
 */
export function parsePackageSearchParams(search: string): PackageSearchValues {
  if (!search) return EMPTY_PACKAGE_SEARCH;

  const params = new URLSearchParams(search);
  const monthYear = params.get('monthYear');
  const [monthPart, yearPart] = (monthYear || '').split('-');
  const month = Number(monthPart);
  const year = Number(yearPart);
  const adults = Number(params.get('adultsQty'));

  return {
    origin: parsePlace(params.get('originId'), params.get('originName')),
    destination: parsePlace(params.get('destinationId'), params.get('destinationName')),
    month: Number.isFinite(month) && month >= 1 && month <= 12 ? month : null,
    year: Number.isFinite(year) && year > 0 ? year : null,
    adults: Number.isFinite(adults) && adults >= 1 ? adults : 1,
  };
}

interface BuildPackageSearchQueryOptions {
  cache: boolean;
  refreshToken: number | string;
}

/**
 * Builds the `/buscarxpaquetes` query string. Returns '' when the departure
 * month is missing, matching the legacy contract where the results route
 * refuses to run without one.
 */
export function buildPackageSearchQuery(
  values: PackageSearchValues,
  options: BuildPackageSearchQueryOptions,
): string {
  if (!values.month || !values.year) return '';

  const params = new URLSearchParams();
  params.set('monthYear', `${String(values.month).padStart(2, '0')}-${values.year}`);

  const originIds = placeIdsParam(values.origin);
  if (originIds && values.origin) {
    params.set('originId', originIds);
    params.set('originName', values.origin.name);
  }

  const destinationIds = placeIdsParam(values.destination);
  if (destinationIds && values.destination) {
    params.set('destinationId', destinationIds);
    params.set('destinationName', values.destination.name);
  }

  if (values.adults) params.set('adultsQty', String(values.adults));

  params.set('cache', String(options.cache));
  params.set('_refresh', String(options.refreshToken));

  return `?${params.toString()}`;
}
